import { db, storage } from "@firebase";
import { COLLECTIONS } from "constant";
import {
  doc,
  setDoc,
  getDoc,
  updateDoc,
  writeBatch,
  where,
  query,
  collection,
  getDocs,
  DocumentReference,
} from "firebase/firestore";
import { getDownloadURL, ref, uploadBytes } from "firebase/storage";
import { throwException } from "utils";
import { v4 as uuidv4 } from "uuid";
import { Content as ContentType } from "../types/types";
class Content {
  /// getContentById
  getContentById = async (id: any) => {
    const docRef = doc(db, "contents", id);
    const docSnap = await getDoc(docRef);
    if (docSnap.exists()) {
      return docSnap.data();
    } else {
      throw new Error("No such document!");
    }
  };

  // Create a new content
  createContents = async (
    uid: string,
    contentsData: ContentType[]
  ): Promise<void> => {
    try {
      const batch = writeBatch(db);
      contentsData.forEach((contentData) => {
        // Convert the `createdBy` to a Firestore DocumentReference
        const userRef = doc(db, COLLECTIONS.USER, uid);

        const contentRef = doc(db, COLLECTIONS.CONTENTS, uuidv4());

        // Add the content data with the `createdBy` reference
        batch.set(contentRef, {
          ...contentData,
          id: contentRef.id,
          createdBy: userRef, // Set `createdBy` as a DocumentReference
        });

        // batch.set(contentRef, contentData);
      });
      await batch.commit();
    } catch (error: any) {
      console.log("error", error);

      throw throwException(error.code || error.message);
    }
  };

  // Update existing content
  updateContent = async (
    uid: string,
    updateData: Partial<ContentType>
  ): Promise<void> => {
    try {
      const contentRef = doc(db, COLLECTIONS.CONTENTS, uid);
      await updateDoc(contentRef, updateData);
    } catch (error: any) {
      throw throwException(error.code || error.message);
    }
  };

  // Get contentsssss by UID
  getContents = async (): Promise<ContentType[]> => {
    try {
      const contentsRef = collection(db, COLLECTIONS.CONTENTS);
      const querySnapshot = await getDocs(contentsRef);

      const contents: ContentType[] = [];
      querySnapshot.forEach((doc) => {
        const contentData = doc.data() as ContentType;
        contentData.id = doc.id; // Attach the document ID to the content
        contents.push(contentData);
      });

      return contents;
    } catch (error: any) {
      throw throwException(error.code || error.message);
    }
  };

  // Get filtered and sorted contents
  getFilteredContents = async (
    category: string | null,
    sortOption: string | null,
    page: number,
    itemsPerPage: number
  ): Promise<ContentType[]> => {
    try {
      const contentsRef = collection(db, COLLECTIONS.CONTENTS);

      // Start with a base query
      let q = query(contentsRef);

      // Add category filter if specified
      if (category) {
        q = query(q, where("category", "==", category));
      }

      // Execute the query
      const querySnapshot = await getDocs(q);

      let contents: ContentType[] = [];
      querySnapshot.forEach((doc) => {
        const contentData = doc.data() as ContentType;
        contents.push({ ...contentData, id: doc.id });
      });

      // Apply sorting logic
      if (sortOption === "Price low to high") {
        contents.sort((a, b) => a.price - b.price);
      } else if (sortOption === "Price high to low") {
        contents.sort((a, b) => b.price - a.price);
      } else if (sortOption === "Best Selling") {
        contents.sort((a, b) => b.purchaseCount - a.purchaseCount);
      }

      // Apply pagination
      const startIndex = (page - 1) * itemsPerPage;
      return contents.slice(startIndex, startIndex + itemsPerPage);
    } catch (error: any) {
      console.error("Error fetching filtered contents:", error);
      throw error;
    }
  };

  // sort contents by data

  // pagination contents by data

  // Get contents by user ID

  getContentsAgainstByUserId = async (
    userId: string
  ): Promise<ContentType[]> => {
    try {
      // Create the exact DocumentReference for the current user
      const userRef = doc(db, COLLECTIONS.USER, userId);

      // Query the contents where "createdBy" matches the DocumentReference
      const contentsRef = collection(db, COLLECTIONS.CONTENTS);
      const q = query(contentsRef, where("createdBy", "==", userRef));

      // Execute the query
      const querySnapshot = await getDocs(q);

      // Map the results to ContentType objects and resolve `createdBy`
      const contents: ContentType[] = await Promise.all(
        querySnapshot.docs.map(async (doc) => {
          const contentData = doc.data() as ContentType;

          // Resolve the `createdBy` DocumentReference
          if (contentData.createdBy instanceof DocumentReference) {
            const createdByDoc = await getDoc(contentData.createdBy);
            if (createdByDoc.exists()) {
              //@ts-ignore
              contentData.createdBy = {
                id: createdByDoc.id,
                ...(createdByDoc.data() || {}),
              }; // Attach the user data
            } else {
              console.warn(
                `No user found for createdBy: ${contentData.createdBy.id}`
              );
              contentData.createdBy = null; // Handle missing user
            }
          }

          return { ...contentData, id: doc.id }; // Include the Firestore document ID
        })
      );

      for await (let element of contents) {
        // Create a DocumentReference for the content
        const contentRef = doc(db, COLLECTIONS.CONTENTS, element.id);

        // Query the purchases where "contentId" matches the DocumentReference
        const qu = query(
          collection(db, COLLECTIONS.PURCHASES),
          where("contentId", "==", contentRef) // Compare with DocumentReference
        );

        // Execute the query
        const querySnapshots = await getDocs(qu);

        const count = querySnapshots.size; // Get the count of documents

        // Calculate total revenue (sum of `amount` fields)
        const totalAmount = querySnapshots.docs.reduce((sum, doc) => {
          const purchaseData = doc.data();
          return sum + (purchaseData.amount || 0); // Add amount if it exists
        }, 0);

        // Add the purchase count to the element (if required)
        element.purchaseCount = count;
        element.revenue = totalAmount;

        querySnapshots.docs.forEach((t) => {
          const newData = t.data();
        });
      }

      return contents;
    } catch (error: any) {
      console.error("Error fetching contents by user ID:", error);
      throw throwException(error.code || error.message);
    }
  };

  // Get contents by user ID
  getContentsByUserId = async (userId: string): Promise<Content[]> => {
    try {
      const userRef = doc(db, COLLECTIONS.USER, userId); // Create a DocumentReference for the user
      const contentsRef = collection(db, COLLECTIONS.CONTENTS);
      const q = query(contentsRef, where("createdBy", "==", userRef)); // Compare with DocumentReference
      const querySnapshot = await getDocs(q);

      const contents: Content[] = [];
      querySnapshot.forEach((doc) => {
        const contentData = { id: doc.id, ...doc.data() } as unknown as Content; // Include the document ID
        contents.push(contentData);
      });

      return contents;
    } catch (error: any) {
      throw throwException(error.code || error.message);
    }
  };

  getUserByCreatedBy = async (createdByRef: DocumentReference) => {
    try {
      const userSnapshot = await getDoc(createdByRef); // Use the DocumentReference directly
      if (userSnapshot.exists()) {
        return { id: createdByRef.id, ...userSnapshot.data() }; // Include the user ID
      } else {
        console.warn(
          "No user found for the provided DocumentReference:",
          createdByRef
        );
        return null;
      }
    } catch (error) {
      console.error(
        "Error fetching user by DocumentReference:",
        createdByRef,
        error
      );
      throw error;
    }
  };

  // Upload profile picture to Firebase Storage and return the download URL
  uploadHandoutPicture = async (uid: string, file: File): Promise<string> => {
    try {
      // Correct the spelling of "assets" and use proper template literal syntax
      const newFilename = uuidv4();
      const storageRef = ref(storage, `handout/${file.name}`);

      // Upload the file to Firebase Storage
      await uploadBytes(storageRef, file);

      // Get and return the download URL
      const downloadURL = await getDownloadURL(storageRef);

      // Optionally, save the download URL in the Firestore content document
      // const contentRef = doc(db, COLLECTIONS.CONTENTS, newFilename);
      // await updateDoc(contentRef, { coverPhotoUrl: downloadURL });

      return downloadURL;
    } catch (error) {
      console.error("Error uploading profile picture:", error);
      throw new Error("Failed to upload profile picture.");
    }
  };

  // update is published content
  updateContentIsPublished = async (
    id: string,
    updateData: Partial<ContentType>
  ) => {
    try {
      const contentRef = doc(db, "contents", id);
      await updateDoc(contentRef, updateData);
      return { success: true };
    } catch (error: any) {
      console.error("Error updating content:", error);
      return { success: false, message: error.message };
    }
  };
}

const ContentsService = new Content();

export { ContentsService };
